import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Title,
  Group,
  TextInput,
  Select,
  Table,
  Checkbox,
  Button,
  Text,
  Badge,
  Tooltip,
  ActionIcon,
  Loader,
  Alert,
  SimpleGrid,
  Card,
} from '@mantine/core';
import {
  IconSearch,
  IconRefresh,
  IconWifi,
  IconWifiOff,
  IconBolt,
  IconServer,
} from '@tabler/icons-react';
import {
  useDeviceStatuses,
  useStatusSummary,
  useCheckDeviceStatusBulk,
  useCheckStatusAll,
  useWakeOnLanBulk,
  type DeviceStatus,
} from '@/hooks/useRemoteManagement';
import { OnlineStatusBadge } from '@/components/devices/OnlineStatusBadge';

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${diffDay}d ago`;
}

export default function DeviceStatusPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: statusData, isLoading, isError, refetch } = useDeviceStatuses();
  const { data: summary } = useStatusSummary();
  const checkStatusBulk = useCheckDeviceStatusBulk();
  const checkStatusAll = useCheckStatusAll();
  const wakeOnLanBulk = useWakeOnLanBulk();

  // Get unique sites for filter
  const sites = useMemo(() => {
    if (!statusData) return [];
    const siteSet = new Set<string>();
    statusData.forEach((s: DeviceStatus) => {
      if (s.site) siteSet.add(s.site);
    });
    return Array.from(siteSet).sort();
  }, [statusData]);

  // Filter devices
  const filteredDevices = useMemo(() => {
    if (!statusData) return [];
    return statusData.filter((device: DeviceStatus) => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          device.hostname?.toLowerCase().includes(searchLower) ||
          device.ipAddress?.toLowerCase().includes(searchLower) ||
          device.primaryMacAddress?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      // Status filter
      if (statusFilter && device.status !== statusFilter) return false;
      // Site filter
      if (siteFilter && device.site !== siteFilter) return false;
      return true;
    });
  }, [statusData, search, statusFilter, siteFilter]);

  // Selection handlers
  const toggleAll = () => {
    if (selectedIds.size === filteredDevices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDevices.map((d: DeviceStatus) => d.deviceId)));
    }
  };

  const toggleOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // Actions
  const handleCheckSelected = () => {
    if (selectedIds.size === 0) return;
    checkStatusBulk.mutate(Array.from(selectedIds));
  };

  const handleCheckAll = () => {
    // Check ALL devices in the database, not just those with existing status records
    checkStatusAll.mutate();
  };

  const handleWakeSelected = () => {
    if (selectedIds.size === 0) return;
    wakeOnLanBulk.mutate({ deviceIds: Array.from(selectedIds) });
  };

  if (isError) {
    return (
      <Stack p="md">
        <Alert color="red" title="Error">
          Failed to load device statuses. Please try again.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack p="md" gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Title order={2}>Device Status</Title>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={handleCheckAll}
            loading={checkStatusAll.isPending}
          >
            Check All
          </Button>
          <ActionIcon variant="subtle" onClick={() => refetch()}>
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Online
              </Text>
              <Text size="xl" fw={700} c="green">
                {summary?.online ?? '-'}
              </Text>
            </div>
            <IconWifi size={32} color="var(--mantine-color-green-6)" />
          </Group>
        </Card>
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Offline
              </Text>
              <Text size="xl" fw={700} c="red">
                {summary?.offline ?? '-'}
              </Text>
            </div>
            <IconWifiOff size={32} color="var(--mantine-color-red-6)" />
          </Group>
        </Card>
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Unknown
              </Text>
              <Text size="xl" fw={700} c="gray">
                {summary?.unknown ?? '-'}
              </Text>
            </div>
            <IconServer size={32} color="var(--mantine-color-gray-5)" />
          </Group>
        </Card>
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Total
              </Text>
              <Text size="xl" fw={700}>
                {summary?.total ?? '-'}
              </Text>
            </div>
            <IconServer size={32} color="var(--mantine-color-blue-6)" />
          </Group>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Paper shadow="xs" p="md" withBorder>
        <Group>
          <TextInput
            placeholder="Search hostname, IP, MAC..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 300 }}
          />
          <Select
            placeholder="Status"
            clearable
            data={[
              { value: 'online', label: 'Online' },
              { value: 'offline', label: 'Offline' },
              { value: 'unknown', label: 'Unknown' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
          />
          <Select
            placeholder="Site"
            clearable
            data={sites.map((s) => ({ value: s, label: s }))}
            value={siteFilter}
            onChange={setSiteFilter}
            style={{ width: 150 }}
          />
          {selectedIds.size > 0 && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {selectedIds.size} selected
              </Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={handleCheckSelected}
                loading={checkStatusBulk.isPending}
              >
                Check Status
              </Button>
              <Button
                size="xs"
                variant="light"
                color="yellow"
                leftSection={<IconBolt size={14} />}
                onClick={handleWakeSelected}
                loading={wakeOnLanBulk.isPending}
              >
                Wake Up
              </Button>
            </Group>
          )}
        </Group>
      </Paper>

      {/* Table */}
      <Paper shadow="xs" withBorder>
        {isLoading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : filteredDevices.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No devices found
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>
                  <Checkbox
                    checked={selectedIds.size === filteredDevices.length && filteredDevices.length > 0}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < filteredDevices.length}
                    onChange={toggleAll}
                  />
                </Table.Th>
                <Table.Th>Hostname</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>IP Address</Table.Th>
                <Table.Th>Site / Room</Table.Th>
                <Table.Th>WinRM</Table.Th>
                <Table.Th>Latency</Table.Th>
                <Table.Th>Last Checked</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredDevices.map((device: DeviceStatus) => (
                <Table.Tr key={device.deviceId}>
                  <Table.Td>
                    <Checkbox
                      checked={selectedIds.has(device.deviceId)}
                      onChange={() => toggleOne(device.deviceId)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>{device.hostname || '-'}</Text>
                    {device.deviceType && (
                      <Text size="xs" c="dimmed">
                        {device.deviceType}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <OnlineStatusBadge
                      status={device.status}
                      deviceStatus={device}
                      size="sm"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {device.ipAddress || '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {device.site ? (
                      <>
                        <Text size="sm">{device.site}</Text>
                        {device.room && (
                          <Text size="xs" c="dimmed">
                            {device.room}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text size="sm" c="dimmed">
                        -
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {device.winrmAvailable ? (
                      <Badge color="green" variant="light" size="sm">
                        Yes
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" size="sm">
                        No
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {device.latencyMs !== null ? (
                      <Text size="sm">{device.latencyMs}ms</Text>
                    ) : (
                      <Text size="sm" c="dimmed">
                        -
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={device.lastCheckedAt || 'Never'}>
                      <Text size="sm">{formatRelativeTime(device.lastCheckedAt)}</Text>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* Footer info */}
      <Text size="xs" c="dimmed" ta="center">
        Auto-refreshes every 30 seconds
      </Text>
    </Stack>
  );
}
